import React, { useState } from 'react';
import AdminSidebar from '../../components/ui/AdminSidebar';
import AuthHeader from '../../components/ui/AuthHeader';
import Icon from '../../components/AppIcon';
import ActivityTable from './components/ActivityTable';
import FilterPanel from './components/FilterPanel';
import ExportModal from './components/ExportModal';
import ActivityStatsPanel from './components/ActivityStatsPanel';

const ActivityLogsMonitor = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [filters, setFilters] = useState({
    dateRange: 'last7days',
    startDate: '',
    endDate: '',
    userType: 'all',
    activityCategory: 'all',
    severityLevel: 'all',
    searchQuery: ''
  });
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'timestamp', direction: 'desc' });

  // Mock activity logs data
  const mockActivityLogs = [
    {
      id: 1,
      timestamp: '2026-02-08 16:45:23',
      clientName: 'Tech Innovations Pvt Ltd',
      userName: 'Rajesh Kumar',
      actionPerformed: 'Generated BMC Report',
      moduleUsed: 'BMC',
      ipAddress: '103.45.67.89',
      status: 'Success',
      severity: 'Info',
      details: 'Business Model Canvas report generated successfully',
      userType: 'Scale Plan'
    },
    {
      id: 2,
      timestamp: '2026-02-08 16:30:15',
      clientName: 'Digital Marketing Hub',
      userName: 'Amit Patel',
      actionPerformed: 'Login Attempt',
      moduleUsed: 'Authentication',
      ipAddress: '103.45.67.90',
      status: 'Success',
      severity: 'Info',
      details: 'User logged in successfully',
      userType: 'Enterprise Plan'
    },
    {
      id: 3,
      timestamp: '2026-02-08 16:15:42',
      clientName: 'Green Earth Solutions',
      userName: 'Priya Sharma',
      actionPerformed: 'Predictive Analysis Run',
      moduleUsed: 'Predictive',
      ipAddress: '103.45.67.91',
      status: 'Success',
      severity: 'Info',
      details: 'Revenue prediction analysis completed',
      userType: 'Growth Plan'
    },
    {
      id: 4,
      timestamp: '2026-02-08 16:00:08',
      clientName: 'Fashion Forward',
      userName: 'Sneha Reddy',
      actionPerformed: 'Payment Failed',
      moduleUsed: 'Payment',
      ipAddress: '103.45.67.92',
      status: 'Failed',
      severity: 'Error',
      details: 'Payment gateway timeout - insufficient funds',
      userType: 'Free Plan'
    },
    {
      id: 5,
      timestamp: '2026-02-08 15:45:30',
      clientName: 'EduTech Academy',
      userName: 'Meera Iyer',
      actionPerformed: 'Goal Plan Created',
      moduleUsed: 'Goal',
      ipAddress: '103.45.67.93',
      status: 'Success',
      severity: 'Info',
      details: 'New quarterly goal plan created',
      userType: 'Scale Plan'
    },
    {
      id: 6,
      timestamp: '2026-02-08 15:30:12',
      clientName: 'HealthCare Plus',
      userName: 'Dr. Vikram Singh',
      actionPerformed: 'Account Suspended',
      moduleUsed: 'Administrative',
      ipAddress: '103.45.67.94',
      status: 'Success',
      severity: 'Warning',
      details: 'Account suspended due to payment failure',
      userType: 'Growth Plan'
    },
    {
      id: 7,
      timestamp: '2026-02-08 15:15:45',
      clientName: 'Tech Innovations Pvt Ltd',
      userName: 'Rajesh Kumar',
      actionPerformed: 'Subscription Upgraded',
      moduleUsed: 'Subscription',
      ipAddress: '103.45.67.89',
      status: 'Success',
      severity: 'Info',
      details: 'Plan upgraded from Growth to Scale',
      userType: 'Scale Plan'
    },
    {
      id: 8,
      timestamp: '2026-02-08 15:00:20',
      clientName: 'Digital Marketing Hub',
      userName: 'Amit Patel',
      actionPerformed: 'API Rate Limit Exceeded',
      moduleUsed: 'API',
      ipAddress: '103.45.67.90',
      status: 'Failed',
      severity: 'Critical',
      details: 'AI usage limit exceeded for current billing cycle',
      userType: 'Enterprise Plan'
    },
    {
      id: 9,
      timestamp: '2026-02-08 14:45:33',
      clientName: 'Green Earth Solutions',
      userName: 'Priya Sharma',
      actionPerformed: 'Report Downloaded',
      moduleUsed: 'Reports',
      ipAddress: '103.45.67.91',
      status: 'Success',
      severity: 'Info',
      details: 'PDF report downloaded successfully',
      userType: 'Growth Plan'
    },
    {
      id: 10,
      timestamp: '2026-02-08 14:30:18',
      clientName: 'Fashion Forward',
      userName: 'Sneha Reddy',
      actionPerformed: 'Login Failed',
      moduleUsed: 'Authentication',
      ipAddress: '103.45.67.92',
      status: 'Failed',
      severity: 'Warning',
      details: 'Invalid credentials - 3rd attempt',
      userType: 'Free Plan'
    },
    {
      id: 11,
      timestamp: '2026-02-08 14:15:55',
      clientName: 'EduTech Academy',
      userName: 'Meera Iyer',
      actionPerformed: 'Password Reset',
      moduleUsed: 'Authentication',
      ipAddress: '103.45.67.93',
      status: 'Success',
      severity: 'Info',
      details: 'Password reset email sent successfully',
      userType: 'Scale Plan'
    },
    {
      id: 12,
      timestamp: '2026-02-08 14:00:42',
      clientName: 'Tech Innovations Pvt Ltd',
      userName: 'Rajesh Kumar',
      actionPerformed: 'Data Export',
      moduleUsed: 'Reports',
      ipAddress: '103.45.67.89',
      status: 'Success',
      severity: 'Info',
      details: 'CSV data export completed',
      userType: 'Scale Plan'
    }
  ];

  // Filter logic
  const filteredLogs = mockActivityLogs?.filter((log) => {
    const matchesSearch = 
      log?.clientName?.toLowerCase()?.includes(filters?.searchQuery?.toLowerCase()) ||
      log?.userName?.toLowerCase()?.includes(filters?.searchQuery?.toLowerCase()) ||
      log?.actionPerformed?.toLowerCase()?.includes(filters?.searchQuery?.toLowerCase());

    const matchesUserType = filters?.userType === 'all' || log?.userType?.toLowerCase()?.includes(filters?.userType?.toLowerCase());
    const matchesCategory = filters?.activityCategory === 'all' || log?.moduleUsed?.toLowerCase() === filters?.activityCategory?.toLowerCase();
    const matchesSeverity = filters?.severityLevel === 'all' || log?.severity?.toLowerCase() === filters?.severityLevel?.toLowerCase();

    return matchesSearch && matchesUserType && matchesCategory && matchesSeverity;
  });

  // Sort logic
  const sortedLogs = [...filteredLogs]?.sort((a, b) => {
    if (sortConfig?.key) {
      const aValue = a?.[sortConfig?.key];
      const bValue = b?.[sortConfig?.key];
      
      if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  // Pagination
  const itemsPerPage = 10;
  const totalPages = Math.ceil(sortedLogs?.length / itemsPerPage);
  const paginatedLogs = sortedLogs?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig?.key === key && sortConfig?.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const handleFilterChange = (newFilters) => {
    setFilters({ ...filters, ...newFilters });
    setCurrentPage(1);
  };

  const handleExport = (config) => {
    console.log('Export config:', config);
    // Implementation will be added with backend integration
    setIsExportModalOpen(false);
  };

  const handleRefreshToggle = () => {
    setAutoRefresh(!autoRefresh);
  };

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <AdminSidebar isCollapsed={isSidebarCollapsed} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <AuthHeader 
          onToggleSidebar={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          isSidebarCollapsed={isSidebarCollapsed}
        />
        
        <main className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground font-heading">
                  Activity Logs Monitor
                </h1>
                <p className="text-sm text-muted-foreground font-caption mt-1">
                  Comprehensive system activity tracking and audit trail
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={handleRefreshToggle}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md transition-smooth elevation-sm ${
                    autoRefresh
                      ? 'bg-success text-white' :'bg-card border border-border text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name="RefreshCw" size={16} className={autoRefresh ? 'animate-spin' : ''} />
                  <span className="text-sm font-medium font-body">
                    {autoRefresh ? 'Live' : 'Refresh'}
                  </span>
                </button>
                <button
                  onClick={() => setIsExportModalOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-smooth elevation-sm"
                >
                  <Icon name="Download" size={16} />
                  <span className="text-sm font-medium font-body">Export Logs</span>
                </button>
              </div>
            </div>

            {/* Activity Statistics */}
            <ActivityStatsPanel logs={mockActivityLogs} />

            {/* Filter Panel */}
            <FilterPanel 
              filters={filters}
              onFilterChange={handleFilterChange}
            />

            {/* Activity Table */}
            <ActivityTable 
              logs={paginatedLogs}
              sortConfig={sortConfig}
              onSort={handleSort}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              totalLogs={sortedLogs?.length}
            />
          </div>
        </main>
      </div>

      {/* Export Modal */}
      {isExportModalOpen && (
        <ExportModal
          onClose={() => setIsExportModalOpen(false)}
          onExport={handleExport}
          totalRecords={sortedLogs?.length}
        />
      )}
    </div>
  );
};

export default ActivityLogsMonitor;